import {
  Box,
  IconButton,
  InputAdornment,
  SelectChangeEvent,
  Stack,
  styled,
  Typography
} from '@mui/material';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PrimaryTableHeader, TableCustom } from '../elements/TableCustom';
import { IOptions, Room, RoomLevelIssues } from 'types/room';
import { ButtonCustom } from '../elements/ButtonCustom';
import { renderRoomLevelIssues } from './tableContent';
import { InputSelect } from '../elements/InputSelect';
import { InputText } from '../elements/InputText';
import { useAuth } from 'contexts/auth';
import { Role } from 'const/common';
import { ModalCreateRoomLevelIssue } from './ModalRoomLevelIssues';
import { createRoomLevelIssue, updateRoomLevelIssue } from 'services/startInspection';
import { UpdateRoomIssues, deleteRoomIssue } from 'services/inspections';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';

interface IRoomLevelIssueProps {
  room: Room;
  tableRoomLevelIssueHeaders: PrimaryTableHeader[];
  optionsLargestInTable: IOptions[];
  getLabelField: (type: string) => string;
  reFreshRoomLevelIssues: () => void | Promise<void>;
}

const RoomLevelIssue = ({
  room,
  tableRoomLevelIssueHeaders,
  optionsLargestInTable,
  reFreshRoomLevelIssues,
  getLabelField
}: IRoomLevelIssueProps) => {
  const { setFlash } = useFlash();
  const [openRoomLevelIssues, setOpenRoomLevelIssues] = useState<boolean>(false);
  const [issuesStatus, setIssuesStatus] = useState('');
  const [valueSearch, setValueSearch] = useState('');
  const [roomLevelIssues, setRoomLevelIssues] = useState<RoomLevelIssues[]>([]);
  const [dataSelect, setDataSelect] = useState<RoomLevelIssues | null>(null);
  const [archiveFilter, setArchiveFilter] = useState('active');
  const valueSearchRef = useRef(valueSearch);
  valueSearchRef.current = valueSearch;

  const { auth } = useAuth();
  const isCanAction = useMemo(
    () => (auth?.role ? [Role.ADMIN, Role.SUPER_ADMIN].includes(auth?.role) : false),
    [auth?.role]
  );

  const computeFilteredRoomIssues = useCallback((): RoomLevelIssues[] => {
    const list = room.roomLevelIssues ?? [];
    const vs = valueSearchRef.current;
    return list.filter((issue) => {
      let resultSearch = true;
      let resultStatus = true;
      let resultArchive = true;

      if (vs)
        resultSearch =
          issue.options.some((op) => op.value?.toLowerCase().includes(vs.toLowerCase())) ||
          issue.actionBy?.toLowerCase().includes(vs.toLowerCase());

      if (issuesStatus) resultStatus = issue.status === issuesStatus;

      resultArchive =
        archiveFilter === 'archived' ? issue.archivedAt !== null : issue.archivedAt === null;

      return resultSearch && resultStatus && resultArchive;
    });
  }, [room.roomLevelIssues, issuesStatus, archiveFilter]);

  useEffect(() => {
    setRoomLevelIssues(computeFilteredRoomIssues());
  }, [computeFilteredRoomIssues]);

  const onSearch = useCallback(() => {
    setRoomLevelIssues(computeFilteredRoomIssues());
  }, [computeFilteredRoomIssues]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCreateIssue = async (payload: any) => {
    if (payload?.id) {
      await updateRoomLevelIssue(payload, room?.id);
      reFreshRoomLevelIssues();
      setFlash({ type: 'success', message: 'Update successful' });
      setOpenRoomLevelIssues(false);
      return;
    }
    await createRoomLevelIssue(payload, room?.id);
    reFreshRoomLevelIssues();
    setFlash({ type: 'success', message: 'Add new successful' });
    setOpenRoomLevelIssues(false);
  };

  const { asyncCallback } = useAsyncCallback(handleCreateIssue, []);

  const handleArchiveRoomLevelIssue = useCallback(
    async (row: RoomLevelIssues) => {
      const wasArchived = Boolean(row.archivedAt);
      try {
        await UpdateRoomIssues({
          submitId: String(row.submitId),
          roomIssuesId: String(row.id),
          payload: { archived: !wasArchived }
        });
        await Promise.resolve(reFreshRoomLevelIssues());
        setFlash({
          type: 'success',
          message: wasArchived ? 'Location issue unarchived.' : 'Location issue archived.'
        });
      } catch {
        setFlash({ type: 'error', message: 'Could not update archive status.' });
      }
    },
    [reFreshRoomLevelIssues, setFlash]
  );

  const handleDeleteFromUpdateModal = useCallback(
    async ({ id, submitId }: { id: number; submitId: number }) => {
      try {
        await deleteRoomIssue({
          submitId: String(submitId),
          roomIssuesId: String(id)
        });
        await Promise.resolve(reFreshRoomLevelIssues());
        setFlash({
          type: 'success',
          message: 'Location level issue removed.'
        });
        setDataSelect(null);
      } catch {
        setFlash({ type: 'error', message: 'Could not delete this location level issue.' });
        throw new Error('delete failed');
      }
    },
    [reFreshRoomLevelIssues, setFlash]
  );

  return (
    <WrapperFilter>
      <Stack justifyContent="space-between" flexDirection={'row'} alignItems="center">
        <TextTitle>Location Level Issues</TextTitle>
        <ButtonCustom
          sx={{ width: '180px' }}
          label={'Add New'}
          onClick={() => {
            setOpenRoomLevelIssues(true);
          }}
        />
      </Stack>
      <Stack alignItems="flex-end" justifyContent="space-between" flexDirection="unset" gap={1.25}>
        <Stack gap={1} flexDirection="unset">
          <InputText
            startAdornment={
              <InputAdornment position="end">
                <IconButton>
                  <img alt={'icon-user'} src={'/icons/icon_search.svg'} width={24} />
                </IconButton>
              </InputAdornment>
            }
            placeholder={'Search'}
            inputProps={{
              value: valueSearch,
              style: {
                width: '352px',
                minHeight: '46px',
                height: '46px',
                padding: 0
              },
              onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setValueSearch(event.target.value);
              },
              onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                if (event.key === 'Enter') {
                  onSearch && onSearch();
                }
              }
            }}
          />
          <ButtonCustom
            sx={{ width: 'max-content', height: '46px' }}
            label={'Search'}
            onClick={onSearch}
          />
        </Stack>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <InputSelect
            value={issuesStatus}
            placeholder={'Status'}
            title={'Status'}
            onChange={(e: SelectChangeEvent<unknown>) => {
              setIssuesStatus(e.target.value as string);
            }}
            sx={{ width: 200, minHeight: '46px', height: '46px', padding: 0 }}
            options={[
              { value: '', label: 'All' },
              { value: 'ACTIVE', label: 'Open' },
              { value: 'RESOLVED', label: 'Resolved' }
            ]}
          />
          <InputSelect
            value={archiveFilter}
            placeholder={'Archive'}
            title={'Archive'}
            onChange={(e: SelectChangeEvent<unknown>) => {
              setArchiveFilter(e.target.value as string);
            }}
            sx={{ width: 200, minHeight: '46px', height: '46px', padding: 0 }}
            options={[
              { value: 'archived', label: 'Archive' },
              { value: 'active', label: 'Active' }
            ]}
          />
        </Box>
      </Stack>

      <TableCustom
        data={renderRoomLevelIssues({
          dataTable: roomLevelIssues,
          navigateDetail: (data: RoomLevelIssues) => {
            setDataSelect(data);
            setOpenRoomLevelIssues(true);
          },
          optionsLargestInTable,
          getLabelField,
          isCanAction,
          onArchive: isCanAction ? handleArchiveRoomLevelIssue : undefined
        })}
        handleChooseRow={() => {}}
        headers={tableRoomLevelIssueHeaders}
        styledTableRow={{ cursor: 'default' }}
        isScrollX
      />
      <ModalCreateRoomLevelIssue
        onCreate={asyncCallback}
        initValue={dataSelect}
        onDeleteIssue={isCanAction ? handleDeleteFromUpdateModal : undefined}
        onClose={() => {
          setOpenRoomLevelIssues(false);
          setDataSelect(null);
        }}
        open={openRoomLevelIssues}
      />
    </WrapperFilter>
  );
};

export default RoomLevelIssue;

const WrapperFilter = styled(Stack)(() => ({
  width: '100%',
  gap: '10px'
}));

const TextTitle = styled(Typography)(() => ({
  color: '#226AAA',
  fontSize: '18px',
  fontWeight: 900
}));
